import { ReactElement, useCallback, useMemo } from "react";
import {
  NavLink,
  useMatch,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { HStack, Link, useColorModeValue } from "@chakra-ui/react";
import { SingleValue } from "react-select";

import { DesktopOnly, MobileOnly } from "components/BreakpointVisibility";
import ConditionCheck from "components/ConditionCheck";
import { Select } from "components/Select";
import {
  contractsPath,
  documentsPath,
  userManagementPath,
} from "constants/paths";
import useFeatureFlag from "hooks/useFeatureFlag";
import { QUERY_STRING_KEYS } from "hooks/useQueryParam";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import { DOCUMENTS, USER_MANAGEMENT } from "util/featureFlags";

export const NavigationTab = ({
  tabName,
  route,
}: {
  tabName: string;
  route: string;
}) => {
  const color = useColorModeValue("grey.500", "grey.400");
  const activeColor = useColorModeValue("black", "white");
  const hoverColor = useColorModeValue("grey.400", "grey.300");

  const selectedCustomerCodes = useSelectedCustomerCode();

  return (
    <Link
      as={NavLink}
      to={`${route}?${
        QUERY_STRING_KEYS.SELECTED_CUSTOMER_KEY
      }=${encodeURIComponent(selectedCustomerCodes.toString())}`}
      color={color}
      fontWeight="800"
      fontSize={32}
      _activeLink={{
        color: activeColor,
        textDecorationLine: "underline",
        textUnderlineOffset: "12px",
      }}
      _hover={{ color: hoverColor, textDecorationLine: "none" }}
    >
      {tabName}
    </Link>
  );
};

const NavigationTabsBar = (): ReactElement => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const match = useMatch(":tab/*");
  const matchTab = match?.params.tab;
  const hasUserAdminFeatureFlag = useFeatureFlag(USER_MANAGEMENT.VIEW);
  const hasDocumentsFeatureFlag = useFeatureFlag(DOCUMENTS.VIEW);

  const tabs = useMemo(
    () => [
      {
        tabName: "Contracts",
        route: contractsPath,
        conditions: [],
      },
      {
        tabName: "Documents",
        route: documentsPath,
        conditions: [hasDocumentsFeatureFlag],
      },
      {
        tabName: "User Management",
        route: userManagementPath,
        conditions: [hasUserAdminFeatureFlag],
      },
    ],
    [hasDocumentsFeatureFlag, hasUserAdminFeatureFlag]
  );

  const selectedTabIndex = useMemo(
    () => tabs.findIndex(({ route }) => route === matchTab),
    [tabs, matchTab]
  );

  const handleTabsChange = useCallback(
    (index: number) => {
      navigate(`${tabs[index]?.route}?${search}`);
    },
    [tabs, navigate, search]
  );

  const tabSelectOptions = useMemo(
    () =>
      tabs
        .map(({ tabName, conditions }, index) =>
          conditions.some((condition) => !condition)
            ? undefined
            : { label: tabName, value: `${index}` }
        )
        .filter(Boolean) as SelectOption[],
    [tabs]
  );

  return (
    <>
      <DesktopOnly>
        <HStack
          alignItems="flex-start"
          display="flex"
          direction="row"
          spacing={10}
        >
          {tabs.map(({ tabName, route, conditions }) => (
            <ConditionCheck key={tabName} conditions={conditions}>
              <NavigationTab tabName={tabName} route={route} />
            </ConditionCheck>
          ))}
        </HStack>
      </DesktopOnly>
      <MobileOnly>
        <Select
          size="sm"
          isClearable={false}
          isSearchable={false}
          options={tabSelectOptions}
          value={tabSelectOptions[selectedTabIndex]}
          onChange={
            ((option: SingleValue<SelectOption>) => {
              handleTabsChange(parseInt(option?.value ?? "0", 10));
            }) as any
          }
        />
      </MobileOnly>
    </>
  );
};

export default NavigationTabsBar;
